import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useMutation } from '@apollo/client';
import { ADD_PROJECT_LEAD_ASSESSMENT, REMOVE_PROJECT_LEAD_ASSESSMENT, UPDATE_PROJECT_LEAD_ASSESSMENT } from './revisions.graphql';
import { Revision } from './Details';
import { StyledPaper } from '../theme';
import { formatDate } from '../Formatters';
import { Unpacked } from '../graphQLTypes/types';

type Assessment = NonNullable<Unpacked<NonNullable<NonNullable<Revision>['projectLeadAssessments']>>>;

type Editing = { mode: 'new' } | { mode: 'edit'; assessment: Assessment } | null;

export const ProjectLeadAssessment: FC<{ revision: Revision; readonly: boolean }> = ({ revision, readonly }) => {
  const [editing, setEditing] = useState<Editing>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Assessment | null>(null);

  const assessments = (revision?.projectLeadAssessments ?? []).filter((x) => !!x);
  const [current, ...history] = assessments;

  const isEditing = (assessment: Assessment) => editing?.mode === 'edit' && editing.assessment.id === assessment.id;

  const renderAssessment = (assessment: Assessment) =>
    isEditing(assessment) ? (
      <AssessmentEditor revisionId={revision?.id} assessment={assessment} onDone={() => setEditing(null)} />
    ) : (
      <AssessmentRow
        assessment={assessment}
        readonly={readonly}
        onEdit={() => setEditing({ mode: 'edit', assessment })}
        onDelete={() => setPendingDelete(assessment)}
      />
    );

  return (
    <StyledPaper>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" component="h3">
          Prosjektoppdatering
        </Typography>
        {!readonly && editing?.mode !== 'new' && (
          <Button size="small" variant="outlined" onClick={() => setEditing({ mode: 'new' })}>
            Ny oppdatering
          </Button>
        )}
      </Stack>

      {editing?.mode === 'new' && (
        <Box sx={{ mb: current ? 2 : 0 }}>
          <AssessmentEditor revisionId={revision?.id} onDone={() => setEditing(null)} />
        </Box>
      )}

      {current && renderAssessment(current)}
      {!current && editing?.mode !== 'new' && <Typography color="textSecondary">Ingen oppdatering er skrevet ennå.</Typography>}

      {history.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Link component="button" type="button" variant="body2" underline="hover" onClick={() => setShowHistory(!showHistory)}>
            {showHistory
              ? 'Skjul tidligere oppdateringer'
              : `Vis ${history.length} tidligere ${history.length === 1 ? 'oppdatering' : 'oppdateringer'}`}
          </Link>
          <Collapse in={showHistory}>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {history.map((assessment) => (
                <Box key={assessment.id}>
                  <Divider sx={{ mb: 1.5 }} />
                  {renderAssessment(assessment)}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      {pendingDelete && <DeleteDialog revisionId={revision?.id} assessment={pendingDelete} onClose={() => setPendingDelete(null)} />}
    </StyledPaper>
  );
};

const AssessmentRow: FC<{
  assessment: Assessment;
  readonly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ assessment, readonly, onEdit, onDelete }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const closeMenu = () => setMenuAnchor(null);

  const pick = (action: () => void) => () => {
    closeMenu();
    action();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
      <Stack spacing={0.5}>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{assessment.text}</Typography>
        <Typography variant="caption" color="textSecondary">
          {[formatDate(assessment.createdAt), assessment.createdByUser?.name].filter((x) => !!x).join(' - ')}
          {assessment.updatedAt && ` (endret ${formatDate(assessment.updatedAt)})`}
        </Typography>
      </Stack>
      {!readonly && (
        <Box sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            title="Handlinger"
            aria-label="Handlinger for oppdateringen">
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
            <MenuItem onClick={pick(onEdit)}>Endre</MenuItem>
            <MenuItem onClick={pick(onDelete)}>Slett</MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

const AssessmentEditor: FC<{ revisionId: string | undefined; assessment?: Assessment; onDone: () => void }> = ({
  revisionId,
  assessment,
  onDone,
}) => {
  const [addAssessment, { loading: adding }] = useMutation(ADD_PROJECT_LEAD_ASSESSMENT);
  const [updateAssessment, { loading: saving }] = useMutation(UPDATE_PROJECT_LEAD_ASSESSMENT);
  const [text, setText] = useState(assessment?.text ?? '');

  const handleSave = async () => {
    if (!text.trim()) return;

    if (assessment) {
      await updateAssessment({ variables: { input: { id: revisionId, assessmentId: assessment.id, text } } });
    } else {
      await addAssessment({ variables: { input: { id: revisionId, text } } });
    }

    onDone();
  };

  const busy = adding || saving;

  return (
    <Box>
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        variant="outlined"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Hvordan ligger revisjonen an?"
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={!text.trim() || busy}>
          Lagre
        </Button>
        <Button color="secondary" onClick={onDone} disabled={busy}>
          Avbryt
        </Button>
      </Stack>
    </Box>
  );
};

const DeleteDialog: FC<{ revisionId: string | undefined; assessment: Assessment; onClose: () => void }> = ({ revisionId, assessment, onClose }) => {
  const [removeAssessment, { loading }] = useMutation(REMOVE_PROJECT_LEAD_ASSESSMENT);

  const handleDelete = async () => {
    await removeAssessment({ variables: { input: { id: revisionId, assessmentId: assessment.id } } });
    onClose();
  };

  return (
    <Dialog open onClose={onClose} aria-labelledby="slett-oppdatering-tittel">
      <DialogTitle id="slett-oppdatering-tittel">Slette oppdatering</DialogTitle>
      <DialogContent>
        <DialogContentText>Oppdateringen blir borte for godt. Er du sikker?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" autoFocus disabled={loading}>
          Nei
        </Button>
        <Button onClick={handleDelete} variant="contained" color="primary" disabled={loading}>
          Ja, slett
        </Button>
      </DialogActions>
    </Dialog>
  );
};
